import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { Navigate, useSearchParams } from "react-router-dom";
import { useInsightsNavigation } from "../navigation/useInsightsNavigation";
import {
  PANEL_DASHBOARD,
  PANEL_DEVICES,
  PANEL_MY_FOCUS,
  PANEL_TICKET_ANALYSIS,
  PANEL_TICKET_LIFECYCLE,
  PANEL_USAGE_INSIGHTS,
  THEO_OVERVIEW_PATH,
  currentAnalyticsPanel,
} from "../navigation/sectionTabDefs";
import theme from "../theme";
import DashboardTicketAnalysisPanel from "../components/DashboardTicketAnalysisPanel";
import { parseDashboardCacheDayParam } from "../mspAnalytics/automatableDashboardShared";
import DashboardMyFocusTab from "../mspAnalytics/DashboardMyFocusTab";
import MspUsageInsightsTab from "../mspAnalytics/MspUsageInsightsTab";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import RmmDevices from "./RmmDevices";
import { isTechnicianDashboardLdEnabled } from "../utils/technicianDashboardAccess";
import { isTicketLifecycleLdEnabled } from "../utils/ticketLifecycleDashboardAccess";
import { isUsageInsightsLdEnabled } from "../utils/usageInsightsAccess";
import { canAccessUsageAnalytics } from "../utils/horizonRoles";
import { useUserMsp } from "../hooks/useUserMsp";
import TicketLifecyclePanel from "../features/ticketLifecycle/TicketLifecyclePanel";

/**
 * Insights analytics panels (My focus, Ticket analysis, Usage Insights, Devices, Ticket Lifecycle).
 * The old dashboard panel was replaced by Theo Overview (`/theo-overview`).
 */
const MspAnalytics: React.FC = () => {
  const { isLoading: userMspLoading, userMspInfo } = useUserMsp();
  const [searchParams, setSearchParams] = useSearchParams();
  const flags = useFeatureFlag();

  const { panelIds } = useInsightsNavigation();
  const technicianTabEnabled = isTechnicianDashboardLdEnabled(flags as Record<string, unknown>);
  const ticketLifecycleEnabled = isTicketLifecycleLdEnabled(flags as Record<string, unknown>);
  const usageInsightsEnabled =
    isUsageInsightsLdEnabled(flags as Record<string, unknown>) && canAccessUsageAnalytics(userMspInfo?.role);

  const dashboardCacheDay = useMemo(() => {
    const raw = searchParams.get("cacheDay") ?? searchParams.get("automatableCacheDay");
    return parseDashboardCacheDayParam(raw);
  }, [searchParams]);


  const rawPanel = currentAnalyticsPanel(searchParams.toString());
  // The old "MSP overview" panel became Theo Overview; its links (and a bare /msp-analytics) go there.
  const isRetiredDashboard = rawPanel === PANEL_DASHBOARD;
  const fallbackPanel = panelIds[0];

  useEffect(() => {
    if (userMspLoading || isRetiredDashboard || !fallbackPanel) {
      return;
    }
    if (!panelIds.includes(rawPanel)) {
      const next = new URLSearchParams(searchParams);
      next.set("panel", fallbackPanel);
      setSearchParams(next, { replace: true });
    }
  }, [fallbackPanel, isRetiredDashboard, panelIds, rawPanel, searchParams, setSearchParams, userMspLoading]);

  const activePanel = panelIds.includes(rawPanel) ? rawPanel : fallbackPanel;

  useEffect(() => {
    document.title =
      rawPanel === PANEL_TICKET_ANALYSIS
        ? "Ticket analysis — Dashboard - Theo"
        : rawPanel === PANEL_USAGE_INSIGHTS
          ? "Usage Insights — Dashboard - Theo"
        : rawPanel === PANEL_MY_FOCUS
          ? "My focus — Dashboard - Theo"
          : rawPanel === PANEL_DEVICES
            ? "Devices — Dashboard - Theo"
            : rawPanel === PANEL_TICKET_LIFECYCLE
              ? "Ticket Lifecycle — Dashboard - Theo"
          : "Dashboard - Theo";
    return () => {
      document.title = "Theo";
    };
  }, [rawPanel]);

  if (isRetiredDashboard) {
    return <Navigate to={THEO_OVERVIEW_PATH} replace />;
  }
  if (!userMspLoading && !fallbackPanel) {
    return <Navigate to="/chat-assistant" replace />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        minHeight: "100%",
        minWidth: 0,
        width: "100%",
        bgcolor: "#F8F9FA",
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          background: theme.palette.background.default,
          maxWidth: activePanel === PANEL_TICKET_ANALYSIS ? "none" : "1400px",
          width: "100%",
          mx: activePanel === PANEL_TICKET_ANALYSIS ? 0 : "auto",
          boxSizing: "border-box",
          flex: 1,
        }}
      >
        {activePanel === PANEL_MY_FOCUS && technicianTabEnabled && <DashboardMyFocusTab />}

        {activePanel === PANEL_TICKET_ANALYSIS && (
          <DashboardTicketAnalysisPanel dashboardCacheDay={dashboardCacheDay} />
        )}

        {activePanel === PANEL_USAGE_INSIGHTS && usageInsightsEnabled && <MspUsageInsightsTab />}

        {activePanel === PANEL_DEVICES && <RmmDevices embedded />}

        {activePanel === PANEL_TICKET_LIFECYCLE && ticketLifecycleEnabled && <TicketLifecyclePanel />}
      </Box>
    </Box>
  );
};

export default MspAnalytics;
